<template>
  <v-container class="my-0 ml-0 mr-n2 pa-0" fluid>
    <v-row density="compact" class="ma-0 pa-0">
      <v-col cols="12" class="ma-0 pa-2">
        <!-- Description can be added here if needed -->
      </v-col>
    </v-row>
    <v-row density="compact" class="ma-0 pa-0">
      <v-col cols="12" density="compact" class="ma-0 pa-2 d-flex align-center">
        <Button buttonClass="ml-1" icon="mdi-plus" v-if="count > 0" :click="openCreateProviderOverlay" size="x-large" tooltip="New Provider" variant="outlined" density="default" color="secondary"></Button>
        <v-spacer></v-spacer>
        <v-tooltip text="Refresh Providers" location="top">
          <template v-slot:activator="{ props }">
            <v-btn v-bind="props" color="primary" variant="text" icon :loading="refreshingProviders" @click="refreshProviders(true, 'manual')">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </template>
        </v-tooltip>
      </v-col>
    </v-row>
    <v-row density="compact" class="ma-0 pa-0" v-if="providerKeys != null">
      <v-col v-for="providerKey in providerKeys" :key="providerKey" :cols="isDesktop ? 4 : 12" class="ma-0 pa-2">
        <ProviderSummaryCard v-if="isDesktop" :providerKey="providerKey" @edit="openEditProviderOverlay" />
        <ProviderCard
          v-else
          :providerKey="providerKey"
          :showTips="showTips"
          :startExpanded="providerKey === expandedProviderKey"
          @saved="handleProviderSaved"
        />
      </v-col>
      <v-col v-if="count === 0" :cols="isDesktop ? 4 : 12" class="ma-0 pa-2">
        <v-card @click="openCreateProviderOverlay" class="ma-0 pa-2 card add-card" color="surface" density="compact" variant="elevated"
          elevation="4" min-height="350">
          <p>New provider</p>
          <v-icon class="add-icon" icon="mdi-plus"></v-icon>
        </v-card>
      </v-col>
    </v-row>
    <ConfigCardOverlay
      v-model="showCreateProviderOverlay"
      title="New Provider"
      @close="closeCreateProviderOverlay"
    >
      <ProviderCard
        isOverlay
        :showTips="showTips"
        @remove-card="closeCreateProviderOverlay"
        @saved="handleProviderSaved"
      />
    </ConfigCardOverlay>
    <ConfigCardOverlay
      v-model="showEditProviderOverlay"
      title="Edit Provider"
      @close="closeEditProviderOverlay"
    >
      <ProviderCard
        v-if="editingProviderKey"
        :providerKey="editingProviderKey"
        isOverlay
        :showTips="showTips"
        :startExpanded="true"
        @remove-card="closeEditProviderOverlay"
        @saved="handleProviderSaved"
      />
    </ConfigCardOverlay>
  </v-container>
</template>

<script lang="ts">
import ProviderCard from '@/components/cards/ProviderCard.vue'
import ProviderSummaryCard from '@/components/cards/ProviderSummaryCard.vue'
import ConfigCardOverlay from '@/components/overlays/ConfigCardOverlay.vue'
import AnalyticsMixin from '@/mixins/AnalyticsMixin'
import Button from '@/components/sub/Button.vue'
import { ProviderConfig } from '@/types/providers'
import type { ConfigRefreshTarget } from '@/store/global'
import { Options, mixins } from 'vue-class-component'
import { mapGetters } from 'vuex'

@Options({
  components: {
    ProviderCard,
    ProviderSummaryCard,
    ConfigCardOverlay,
    Button
  },
  computed: {
    ...mapGetters(['providerConfigs', 'selectedTab', 'configRefreshToken'])
  },
  watch: {
    selectedTab(value: string) {
      if (value === 'providers') {
        void this.maybeRefreshProviders()
      }
    },
    providerRefreshToken() {
      void this.maybeRefreshProviders()
    }
  }
})
export default class Providers extends mixins(AnalyticsMixin) {
  public showTips = false
  public providerConfigs!: Record<string, ProviderConfig>
  public selectedTab!: string
  public configRefreshToken!: (target: ConfigRefreshTarget) => number
  public showCreateProviderOverlay = false
  public showEditProviderOverlay = false
  public editingProviderKey: string | null = null
  public expandedProviderKey: string | null = null
  public refreshingProviders = false
  public lastHandledProviderRefreshToken = -1

  get providerKeys(): string[] {
    return this.providerConfigs ? Object.keys(this.providerConfigs) : []
  }

  get providerRefreshToken(): number {
    return this.configRefreshToken('providers')
  }
  
  get count(): number {
    return this.providerKeys.length
  }

  get isDesktop(): boolean {
    return (this as any).$vuetify.display.mdAndUp
  }
  
  async mounted() {
    if (this.selectedTab === 'providers') {
      await this.refreshProviders(true, 'initial')
    }

    this.trackComponentView('providers_tab', {
      provider_count: this.count
    })
  }

  openCreateProviderOverlay() {
    this.trackButtonClick('create_provider_start', 'click', {
      provider_count: this.count
    })
    this.showCreateProviderOverlay = true
  }

  closeCreateProviderOverlay() {
    this.showCreateProviderOverlay = false
  }

  openEditProviderOverlay(providerKey: string) {
    this.editingProviderKey = providerKey
    this.showEditProviderOverlay = true
  }

  closeEditProviderOverlay() {
    this.showEditProviderOverlay = false
    this.editingProviderKey = null
  }

  async refreshProviders(force = false, source: 'initial' | 'manual' | 'sync' = 'sync') {
    if (this.refreshingProviders) return

    if (source === 'manual') {
      this.trackButtonClick('refresh_providers', 'click', {
        provider_count: this.count
      })
    }

    this.refreshingProviders = true
    try {
      await this.$store.dispatch('fetchProviders')
      this.lastHandledProviderRefreshToken = this.providerRefreshToken
    } finally {
      this.refreshingProviders = false
    }
  }

  async maybeRefreshProviders(force = false) {
    if (this.selectedTab !== 'providers' && !force) return
    const needsRefresh = force || this.lastHandledProviderRefreshToken !== this.providerRefreshToken
    if (needsRefresh) {
      await this.refreshProviders(force, force ? 'manual' : 'sync')
    }
  }

  handleProviderSaved(payload?: { providerKey?: string }) {
    if (payload?.providerKey) {
      this.expandedProviderKey = String(payload.providerKey)
    }
    this.lastHandledProviderRefreshToken = this.providerRefreshToken
    this.showCreateProviderOverlay = false
    this.showEditProviderOverlay = false
  }
}
</script>

<style scoped lang="scss">
.add-card {
  border: 1px solid rgba(255, 255, 255, 0.3);
  height: 350px;
  text-align: center;
  cursor: pointer;

  p {
    margin-top: 130px;
  }
}

.add-card-small {
  border: 1px solid rgba(255, 255, 255, 0.3);
  text-align: center;
  cursor: pointer;
}

.add-icon {
  font-size: 24px;
  margin-top: 8px;
}
</style>
